import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Calendar, Star, Upload, X, Image as ImageIcon, FileText, CheckCircle, Clock } from 'lucide-react';
import { getChildTheme } from '../childThemes';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ChildAssignments = () => {
  const [child, setChild] = useState(null);
  const [theme, setTheme] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('all'); // Add subject filter state
  const [dateFilter, setDateFilter] = useState('returned'); // Add date filter: 'overdue', 'today', 'thisweek', 'returned'
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [noPhotoCheckbox, setNoPhotoCheckbox] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken for authenticated requests
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    const childTheme = getChildTheme(childData.theme || 'purple');
    setTheme(childTheme);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadChildAssignments(childData.id);
  }, [navigate]);

  const loadChildAssignments = async (childId) => {
    try {
      const [assignmentsData, subjectsData] = await Promise.all([
        api.getAssignments(childId),
        api.getSubjects()
      ]);
      
      console.log('Loaded assignments for child:', childId);
      console.log('Total assignments:', assignmentsData.length);
      
      // Remove duplicates based on title + due_date + child_id (not just ID)
      const seen = new Set();
      const uniqueAssignments = assignmentsData.filter(a => {
        const key = `${a.child_id}-${a.title}-${a.due_date}`;
        if (seen.has(key)) {
          console.log('DUPLICATE REMOVED:', a.title, 'due:', a.due_date, 'id:', a.id);
          return false;
        }
        seen.add(key);
        return true;
      });
      
      console.log('After deduplication:', uniqueAssignments.length, '(removed', assignmentsData.length - uniqueAssignments.length, 'duplicates)');
      
      setAssignments(uniqueAssignments);
      // Sort subjects alphabetically
      const sortedSubjects = subjectsData.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setSubjects(sortedSubjects);
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load assignments',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!child || !theme) return null;

  // Get today's date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Filter assignments by selected subject first
  // Need to handle both direct ID match and replaced default subjects
  const subjectFilteredAssignments = selectedSubject === 'all' 
    ? assignments 
    : assignments.filter(a => {
        const assignmentSubjectId = String(a.subject_id);
        const selectedSubjectId = String(selectedSubject);
        
        // Direct ID match
        if (assignmentSubjectId === selectedSubjectId) {
          return true;
        }
        
        // Check if selected subject replaces the assignment's subject_id
        const selectedSubjectData = subjects.find(s => String(s.id) === selectedSubjectId);
        if (selectedSubjectData && selectedSubjectData.replaces_default_id) {
          return String(selectedSubjectData.replaces_default_id) === assignmentSubjectId;
        }
        
        return false;
      });

  // Only show assigned work (not submitted or graded) for date filters
  const assignedWork = subjectFilteredAssignments.filter(a => a.status === 'assigned');

  // Returned work - assignments sent back by parents for resubmission
  const returnedWork = subjectFilteredAssignments.filter(a => {
    // Debug: check for send_back_notes
    if (a.send_back_notes) {
      console.log('Found assignment with send_back_notes:', {
        title: a.title,
        status: a.status,
        send_back_notes: a.send_back_notes,
        has_send_back_notes: !!a.send_back_notes
      });
    }
    return a.status === 'assigned' && a.send_back_notes;
  }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)); // Most recent first

  // Debug log
  console.log('Total assignments:', subjectFilteredAssignments.length);
  console.log('Returned work count:', returnedWork.length);
  console.log('Assignments with status "assigned":', subjectFilteredAssignments.filter(a => a.status === 'assigned').length);

  // Categorize by due date
  const overdueWork = assignedWork.filter(a => {
    const dueDate = new Date(a.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today && !a.send_back_notes; // Exclude returned items from overdue
  });

  const dueTodayWork = assignedWork.filter(a => {
    const dueDate = new Date(a.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate.getTime() === today.getTime() && !a.send_back_notes; // Exclude returned items
  });

  // Calculate Monday-Friday of current week
  const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysUntilMonday = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek; // If Sunday, go back 6 days to Monday
  const monday = new Date(today);
  monday.setDate(today.getDate() + daysUntilMonday);
  monday.setHours(0, 0, 0, 0);
  
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4); // Monday + 4 days = Friday
  friday.setHours(23, 59, 59, 999);

  const dueThisWeekWork = assignedWork.filter(a => {
    const dueDate = new Date(a.due_date);
    dueDate.setHours(0, 0, 0, 0);
    // Include today if it's between Monday-Friday
    return dueDate >= monday && dueDate <= friday && !a.send_back_notes; // Exclude returned items
  }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date)); // Ascending for future dates

  // Sort overdue work with most recent due date first (closest to today at top)
  const sortedOverdueWork = [...overdueWork].sort((a, b) => {
    return new Date(b.due_date) - new Date(a.due_date); // Descending - most recent overdue first
  });
  
  // Due today doesn't need sorting, but let's sort by assignment title for consistency
  const sortedDueTodayWork = [...dueTodayWork].sort((a, b) => a.title.localeCompare(b.title));

  // Determine which work to display based on filter
  const displayedWork = dateFilter === 'overdue' ? sortedOverdueWork 
    : dateFilter === 'today' ? sortedDueTodayWork 
    : dateFilter === 'thisweek' ? dueThisWeekWork
    : returnedWork; // Changed from todoWork to returnedWork

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      console.log('Submitting assignment:', selectedAssignment.id);
      console.log('Child:', child);
      console.log('Files:', selectedFiles);
      
      // Upload files to S3 and get URLs
      const attachmentsPromises = selectedFiles.map(async (file) => {
        try {
          const uploadResult = await api.uploadFile(file);
          return {
            type: file.type?.startsWith('image/') ? 'image' : 'document',
            name: file.name,
            url: uploadResult.url
          };
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          throw new Error(`Failed to upload ${file.name}`);
        }
      });

      const attachments = await Promise.all(attachmentsPromises);
      
      await api.submitAssignment(selectedAssignment.id, {
        description: notes,
        attachments: attachments
      });

      toast({
        title: 'Assignment Submitted! 🎉',
        description: `Great job! Your work has been sent to your parent.`
      });

      // Reload assignments
      await loadChildAssignments(child.id);

      setIsSubmitting(false);
      setSelectedAssignment(null);
      setSelectedFiles([]);
      setNoPhotoCheckbox(false);
      setNotes('');
    } catch (error) {
      console.error('Failed to submit assignment:', error);
      toast({
        title: 'Submission Failed',
        description: error.message || 'Could not submit assignment. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const getSubject = (subjectId) => subjects.find(s => s.id === subjectId);

  const AssignmentCard = ({ assignment }) => {
    const subject = getSubject(assignment.subject_id);
    const isOverdue = new Date(assignment.due_date) < new Date() && assignment.status === 'assigned';

    return (
      <Card className="p-5 border-0 shadow-lg hover:shadow-xl transition-all bg-white">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0"
            style={{ backgroundColor: `${subject?.color}20`, color: subject?.color }}
          >
            {subject?.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-slate-900 break-words">{assignment.title}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-slate-500">{subject?.name}</p>
                  {assignment.lesson_plan_title && (
                    <>
                      <span className="text-slate-300">•</span>
                      <p className="text-sm text-slate-600 font-medium">{assignment.lesson_plan_title}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 flex-shrink-0">
                {assignment.status === 'assigned' && (
                  <Badge className={isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    <Clock className="w-3 h-3 mr-1" />
                    {isOverdue ? 'Overdue' : 'To Do'}
                  </Badge>
                )}
                {assignment.status === 'submitted' && (
                  <Badge className="bg-blue-100 text-blue-700">
                    <Clock className="w-3 h-3 mr-1" />
                    Submitted
                  </Badge>
                )}
                {assignment.status === 'graded' && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Graded: {assignment.grade}
                  </Badge>
                )}
              </div>
            </div>
            {assignment.description && (
              <div className="text-slate-700 mb-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: assignment.description }} />
            )}
            
            {/* Attachments */}
            {assignment.attachments && assignment.attachments.length > 0 && (
              <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p className="text-sm font-semibold text-blue-700 mb-2">📎 Files to Download:</p>
                <div className="space-y-2">
                  {assignment.attachments.map((attachment, idx) => (
                    <a
                      key={idx}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-white rounded hover:bg-blue-100 transition-colors"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-700 hover:underline">{attachment.name || attachment.filename || 'Download File'}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Checklist - Progress Tracker & Resources */}
            {assignment.checklist && assignment.checklist.length > 0 && (
              <div className="mb-3 space-y-3">
                {[
                  { key: 'progress', label: '✓ Progress Tracker', border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-700' },
                  { key: 'resources', label: '📚 Resources', border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' },
                ].map(section => {
                  const sectionItems = assignment.checklist
                    .map((it, i) => ({ it, i }))
                    .filter(({ it }) => (it.section || 'progress') === section.key);
                  if (sectionItems.length === 0) return null;
                  return (
                    <div key={section.key} className={`p-3 ${section.bg} border-l-4 ${section.border} rounded`} data-testid={`child-${section.key}-section`}>
                      <p className={`text-sm font-semibold ${section.text} mb-2`}>{section.label}</p>
                      <div className="space-y-2">
                        {sectionItems.map(({ it: item, i: idx }) => (
                          <div key={item.id || idx} className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={item.completed || false}
                              onChange={async (e) => {
                                const updatedChecklist = [...assignment.checklist];
                                updatedChecklist[idx] = { ...item, completed: e.target.checked };
                                try {
                                  await api.updateAssignment(assignment.id, { checklist: updatedChecklist });
                                  const updatedAssignments = assignments.map(a =>
                                    a.id === assignment.id ? { ...a, checklist: updatedChecklist } : a
                                  );
                                  setAssignments(updatedAssignments);
                                } catch (error) {
                                  console.error('Failed to update checklist:', error);
                                  e.target.checked = !e.target.checked;
                                }
                              }}
                              className="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer"
                            />
                            <span className="text-sm text-slate-700">
                              {item.text && item.text.includes('http') ? (
                                <span>
                                  {item.text.split(' ').map((word, i) =>
                                    word.startsWith('http') ? (
                                      <a key={i} href={word} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {word}
                                      </a>
                                    ) : (
                                      <span key={i}> {word}</span>
                                    )
                                  )}
                                </span>
                              ) : (
                                item.text
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {assignment.send_back_notes && (
              <div className="mb-3 p-3 bg-purple-50 border-l-4 border-purple-500 rounded">
                <p className="text-sm font-semibold text-purple-700 mb-1">📝 Parent Notes:</p>
                <p className="text-sm text-slate-700">{assignment.send_back_notes}</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="font-semibold">{assignment.tokens_reward} tokens</span>
                </div>
              </div>
              {assignment.status === 'assigned' && (
                <Button
                  className={`bg-gradient-to-r ${theme.buttonPrimary} text-white w-full sm:w-auto`}
                  onClick={() => {
                    setSelectedAssignment(assignment);
                    setIsSubmitting(true);
                  }}
                >
                  Submit Work
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.cardBg}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.headerBg} text-white p-6 shadow-lg`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/child/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            {/* Profile Photo */}
            <div
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer"
              style={{ color: child.profileColor }}
              onClick={() => navigate('/child/dashboard')}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                child.name.charAt(0)
              )}
            </div>
          </div>
          <h1 className="text-3xl font-bold">My Assignments</h1>
          <p className={`text-${theme.accentColor === 'purple-600' ? 'purple' : 'blue'}-100`}>
            Complete your work and earn tokens!
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Date Filter Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <button
            onClick={() => setDateFilter('overdue')}
            className={`p-4 rounded-lg border-2 transition-all ${
              dateFilter === 'overdue'
                ? 'bg-red-50 border-red-500 shadow-md'
                : 'bg-white border-red-200 hover:border-red-300'
            }`}
          >
            <p className={`text-3xl font-bold ${dateFilter === 'overdue' ? 'text-red-700' : 'text-red-600'}`}>
              {overdueWork.length}
            </p>
            <p className={`text-sm font-semibold ${dateFilter === 'overdue' ? 'text-red-600' : 'text-red-500'}`}>
              Overdue
            </p>
          </button>

          <button
            onClick={() => setDateFilter('today')}
            className={`p-4 rounded-lg border-2 transition-all ${
              dateFilter === 'today'
                ? 'bg-amber-50 border-amber-500 shadow-md'
                : 'bg-white border-amber-200 hover:border-amber-300'
            }`}
          >
            <p className={`text-3xl font-bold ${dateFilter === 'today' ? 'text-amber-700' : 'text-amber-600'}`}>
              {dueTodayWork.length}
            </p>
            <p className={`text-sm font-semibold ${dateFilter === 'today' ? 'text-amber-600' : 'text-amber-500'}`}>
              Due Today
            </p>
          </button>

          <button
            onClick={() => setDateFilter('thisweek')}
            className={`p-4 rounded-lg border-2 transition-all ${
              dateFilter === 'thisweek'
                ? 'bg-purple-50 border-purple-500 shadow-md'
                : 'bg-white border-purple-200 hover:border-purple-300'
            }`}
          >
            <p className={`text-3xl font-bold ${dateFilter === 'thisweek' ? 'text-purple-700' : 'text-purple-600'}`}>
              {dueThisWeekWork.length}
            </p>
            <p className={`text-sm font-semibold ${dateFilter === 'thisweek' ? 'text-purple-600' : 'text-purple-500'}`}>
              Due This Week
            </p>
          </button>

          <button
            onClick={() => setDateFilter('returned')}
            className={`p-4 rounded-lg border-2 transition-all ${
              dateFilter === 'returned'
                ? 'bg-purple-50 border-purple-500 shadow-md'
                : 'bg-white border-purple-200 hover:border-purple-300'
            }`}
          >
            <p className={`text-3xl font-bold ${dateFilter === 'returned' ? 'text-purple-700' : 'text-purple-600'}`}>
              {returnedWork.length}
            </p>
            <p className={`text-sm font-semibold ${dateFilter === 'returned' ? 'text-purple-600' : 'text-purple-500'}`}>
              Returned
            </p>
          </button>
        </div>

        {/* Subject Filter */}
        <Card className="p-4 mb-6 bg-white">
          <div className="flex items-center gap-4">
            <Label className="text-sm font-semibold text-slate-700 whitespace-nowrap">Filter by Subject:</Label>
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All Subjects
                </SelectItem>
                {subjects.map(subject => (
                  <SelectItem key={subject.id} value={String(subject.id)}>
                    {subject.icon} {subject.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedSubject !== 'all' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedSubject('all')}
                className="text-slate-600 hover:text-slate-900"
              >
                Clear Filter
              </Button>
            )}
          </div>
        </Card>

        {/* Filtered Assignments */}
        {displayedWork.length > 0 ? (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {dateFilter === 'overdue' && '⚠️ Overdue Assignments'}
              {dateFilter === 'today' && '📅 Due Today'}
              {dateFilter === 'thisweek' && '📆 Due This Week'}
              {dateFilter === 'returned' && '🔄 Returned - Needs Resubmission'}
            </h2>
            <div className="space-y-4">
              {displayedWork.map(assignment => (
                <AssignmentCard key={assignment.id} assignment={assignment} />
              ))}
            </div>
          </div>
        ) : (
          <Card className="p-12 text-center">
            <p className="text-2xl text-slate-500 mb-2">
              {dateFilter === 'overdue' && 'No overdue assignments! 🎉'}
              {dateFilter === 'today' && 'Nothing due today!'}
              {dateFilter === 'thisweek' && 'Nothing due this week!'}
              {dateFilter === 'returned' && 'No returned assignments! ✅'}
            </p>
            <p className="text-slate-400">
              {dateFilter === 'overdue' && "You're all caught up!"}
              {dateFilter === 'today' && 'Check back later or work ahead!'}
              {dateFilter === 'thisweek' && 'Relax and enjoy your week!'}
              {dateFilter === 'returned' && 'All your work has been accepted!'}
            </p>
          </Card>
        )}

        {assignments.length === 0 && (
          <Card className="p-12 text-center">
            <p className="text-2xl text-slate-500 mb-2">No assignments yet!</p>
            <p className="text-slate-400">Your parent will add assignments for you.</p>
          </Card>
        )}
      </div>

      {/* Submit Dialog */}
      <Dialog open={isSubmitting} onOpenChange={setIsSubmitting}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              Submit: {selectedAssignment?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedAssignment && (
            <>
              <div className="space-y-4 mt-4 overflow-y-auto pr-2 flex-1">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">Assignment:</p>
                  {selectedAssignment.description && (
                    <div className="text-slate-900 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedAssignment.description }} />
                  )}
                </div>

                <div>
                  <Label>Upload Your Work</Label>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                  {/* Take Photo Button */}
                  <div className={`border-2 border-dashed border-${theme.accentColor.split('-')[0]}-300 rounded-xl p-6 text-center hover:border-${theme.accentColor.split('-')[0]}-400 transition-colors cursor-pointer bg-${theme.accentColor.split('-')[0]}-50/50`}>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="camera-upload"
                    />
                    <label htmlFor="camera-upload" className="cursor-pointer">
                      <div className={`w-16 h-16 bg-${theme.accentColor.split('-')[0]}-200 rounded-full flex items-center justify-center mx-auto mb-3`}>
                        <svg className={`w-10 h-10 text-${theme.accentColor.split('-')[0]}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <p className={`text-${theme.accentColor.split('-')[0]}-700 font-semibold`}>Take Photo</p>
                      <p className={`text-${theme.accentColor.split('-')[0]}-500 text-xs mt-1`}>Use camera</p>
                    </label>
                  </div>

                  {/* Choose from Gallery Button */}
                  <div className={`border-2 border-dashed border-${theme.accentColor.split('-')[0]}-300 rounded-xl p-6 text-center hover:border-${theme.accentColor.split('-')[0]}-400 transition-colors cursor-pointer bg-${theme.accentColor.split('-')[0]}-50/50`}>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="assignment-upload"
                    />
                    <label htmlFor="assignment-upload" className="cursor-pointer">
                      <div className={`w-16 h-16 bg-${theme.secondaryColor.split('-')[0]}-200 rounded-full flex items-center justify-center mx-auto mb-3`}>
                        <Upload className={`w-10 h-10 text-${theme.secondaryColor.split('-')[0]}-600`} />
                      </div>
                      <p className={`text-${theme.accentColor.split('-')[0]}-700 font-semibold`}>Choose Files</p>
                      <p className={`text-${theme.accentColor.split('-')[0]}-500 text-xs mt-1`}>From gallery or files</p>
                    </label>
                  </div>
                </div>

                {/* No Photo Checkbox */}
                <div className="mt-4 flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <input
                    type="checkbox"
                    id="no-photo-checkbox"
                    checked={noPhotoCheckbox}
                    onChange={(e) => {
                      setNoPhotoCheckbox(e.target.checked);
                      if (e.target.checked) {
                        setSelectedFiles([]);
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="no-photo-checkbox" className="text-sm text-slate-700 cursor-pointer">
                    No photo needed for this assignment
                  </label>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className={`flex items-center justify-between p-2 bg-${theme.accentColor.split('-')[0]}-50 rounded border border-${theme.accentColor.split('-')[0]}-200`}>
                        <div className="flex items-center gap-2">
                          {file.type.startsWith('image/') ? (
                            <ImageIcon className="w-4 h-4 text-blue-600" />
                          ) : (
                            <FileText className="w-4 h-4 text-slate-600" />
                          )}
                          <span className="text-sm text-slate-700">{file.name}</span>
                        </div>
                        <button onClick={() => removeFile(index)} className="text-red-600 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </div>
              
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Tell your parent anything about this work..."
                  rows={3}
                  className="mt-2"
                />
              </div>
              
              {/* Action buttons fixed at bottom */}
              <div className="flex gap-3 pt-4 border-t mt-4 flex-shrink-0">
                <Button
                  onClick={handleSubmit}
                  className={`flex-1 bg-gradient-to-r ${theme?.buttonPrimary || 'from-purple-600 to-pink-600'} text-white py-6 text-lg`}
                  disabled={selectedFiles.length === 0 && !noPhotoCheckbox}
                >
                  Submit Assignment
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsSubmitting(false);
                    setSelectedFiles([]);
                    setNoPhotoCheckbox(false);
                    setNotes('');
                  }}
                  className="px-6"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChildAssignments;
